import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Target, Globe, Lightbulb, Palette, Users, Shield, ThumbsUp,
  Rocket, CheckCircle, Star, Zap, TrendingUp, Award, ChevronDown
} from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: 'easeOut' },
  }),
};

const TeamCard = ({ member, size = 'md' }) => {
  const [imgError, setImgError] = useState(false);
  const isLg = size === 'lg';

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      whileHover={{ y: -8, scale: 1.03 }}
      className="flex flex-col items-center text-center group"
    >
      {/* Photo ring */}
      <div
        className={`relative ${isLg ? 'w-36 h-36' : 'w-28 h-28'} rounded-full mb-5`}
        style={{
          background: 'linear-gradient(135deg, #f59e0b, #1d4ed8)',
          padding: '3px',
        }}
      >
        <div className="w-full h-full rounded-full overflow-hidden bg-blue-900">
          {member.photo && !imgError ? (
            <img
              src={member.photo}
              alt={member.name}
              className="w-full h-full object-cover object-top"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800">
              <span className={`${isLg ? 'text-4xl' : 'text-3xl'} font-black text-white`}>
                {member.name.charAt(0)}
              </span>
            </div>
          )}
        </div>
        {/* Glow ring on hover */}
        <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ boxShadow: '0 0 24px 6px rgba(245,158,11,0.5)' }} />
      </div>

      {/* Info card */}
      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-5 py-4 w-full hover:bg-white/20 transition-all duration-300">
        <h3 className={`text-white font-bold ${isLg ? 'text-lg' : 'text-sm'} leading-tight mb-1`}>
          {member.name}
        </h3>
        <p className="text-yellow-400 font-semibold text-xs mb-2">{member.role}</p>
        <span className="inline-block bg-yellow-400/20 text-yellow-300 text-xs px-3 py-1 rounded-full border border-yellow-400/30 mb-3">
          {member.tag}
        </span>
        <p className="text-blue-100 text-xs leading-relaxed">{member.about}</p>
      </div>
    </motion.div>
  );
};

const AboutPage = () => {
  useSEO({
    title: 'About Us | AGO Innovations — Our Team, Mission & Vision',
    description:
      'Learn about AGO Innovations — founded by Aditya Sanjay Gurav, Vedant Kirtane, Urvi Torne, and Yash Bahamre. Our mission is to deliver innovative student services and finance solutions in India.',
    keywords:
      'about AGO Innovations, Aditya Gurav, AGO team, AGO Innovations founders, student services company India, innovation company Mumbai',
    canonical: 'https://agoinnovations.in/about',
  });

  const leadershipTeam = [
    {
      name: 'Aditya Sanjay Gurav',
      role: 'Founder & CEO',
      tag: 'Visionary Leader',
      photo: '/aditya-gurav.jpg.jpeg',
      about:
        'Aditya is the visionary founder of Agoinnovations. As CEO, he leads with a strong focus on innovation, growth, and strategic direction — the driving force behind the company\'s success.',
    },
    {
      name: 'Vedant Vishal Kirtane',
      role: 'Co-Founder & CTO',
      tag: 'Tech Innovator',
      photo: '/vedant-kirtane.jpg.jpeg',
      about:
        'Vedant leads the technological development of the company. As CTO, he implements innovative tech solutions and ensures the company stays ahead in the digital space.',
    },
    {
      name: 'Urvi Torne',
      role: 'Co-Founder & CMO',
      tag: 'Marketing Expert',
      photo: '/urvi-torne.jpg.jpeg',
      about:
        'Urvi handles marketing strategies and brand growth. As CMO, she builds strong brand presence and drives customer engagement through creative marketing approaches.',
    },
    {
      name: 'Yash Bahamre',
      role: 'Co-Founder & SMM',
      tag: 'Social Media Strategist',
      photo: '/yash-bhamre.jpg.jpeg',
      about:
        'Yash manages the company\'s social media presence, ensuring strong digital visibility, audience engagement, and effective communication across all platforms.',
    },
  ];

  const management = [
    {
      name: 'Vedant Gade',
      role: 'Deputy CTO',
      tag: 'Technical Leader',
      photo: '/vedant-gade.jpg.jpeg.jpeg',
      about:
        'Vedant supports the technical operations of the company. As Deputy CTO, he plays a key role in executing technical strategies and maintaining system efficiency.',
    },
  ];

  const values = [
    { icon: <Lightbulb className="w-7 h-7" />, label: 'Innovation', color: 'from-yellow-400 to-orange-500' },
    { icon: <Palette className="w-7 h-7" />, label: 'Creativity', color: 'from-pink-400 to-purple-500' },
    { icon: <Users className="w-7 h-7" />, label: 'Teamwork', color: 'from-blue-400 to-cyan-500' },
    { icon: <Shield className="w-7 h-7" />, label: 'Integrity', color: 'from-blue-600 to-blue-800' },
    { icon: <ThumbsUp className="w-7 h-7" />, label: 'Customer Satisfaction', color: 'from-emerald-400 to-teal-500' },
  ];

  const whyUs = [
    { icon: <Lightbulb className="w-5 h-5" />, text: 'Innovative and unique approach' },
    { icon: <Star className="w-5 h-5" />, text: 'Strong leadership team' },
    { icon: <Zap className="w-5 h-5" />, text: 'Technology-driven solutions' },
    { icon: <ThumbsUp className="w-5 h-5" />, text: 'Customer-focused services' },
    { icon: <CheckCircle className="w-5 h-5" />, text: 'Reliable and efficient execution' },
  ];

  const whatWeDo = [
    { icon: <Rocket className="w-6 h-6" />, title: 'Innovative Business Solutions', color: 'from-yellow-400 to-orange-500' },
    { icon: <Zap className="w-6 h-6" />, title: 'Technology-Driven Services', color: 'from-blue-400 to-blue-600' },
    { icon: <TrendingUp className="w-6 h-6" />, title: 'Digital Growth Strategies', color: 'from-emerald-400 to-teal-500' },
    { icon: <Award className="w-6 h-6" />, title: 'Creative Problem-Solving', color: 'from-purple-400 to-pink-500' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0d2045] to-[#0a1628] text-white overflow-hidden">

      {/* ── HERO BANNER ── */}
      <section className="relative pt-28 pb-20 px-4 text-center overflow-hidden">
        {/* Animated blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />
        </div>

        {/* Decorative top bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-blue-500 to-yellow-400" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-4xl mx-auto"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold mb-6"
          >
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            Welcome to Agoinnovations
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
          </motion.div>

          <h1 className="font-black mb-6 leading-tight text-center w-full px-2">
            <span className="text-white block text-4xl sm:text-5xl md:text-7xl">
              ABOUT US –
            </span>
            <span
              className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent block"
              style={{ fontSize: 'clamp(1.4rem, 8vw, 4.5rem)' }}
            >
              AGOINNOVATIONS
            </span>
          </h1>

          <p className="text-blue-100 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-8">
            <span className="text-yellow-400 font-bold">Agoinnovations</span> is a dynamic and forward-thinking company
            dedicated to delivering innovative and practical solutions that solve real-world problems. We combine{' '}
            <span className="text-blue-300 font-semibold">creativity</span>,{' '}
            <span className="text-blue-300 font-semibold">technology</span>, and{' '}
            <span className="text-blue-300 font-semibold">strategic thinking</span> to help businesses grow, adapt, and
            succeed in a competitive environment.
          </p>

          <p className="text-blue-200 text-base italic max-w-2xl mx-auto">
            "Our aim is to transform ideas into impactful solutions that create value and drive progress."
          </p>

          {/* Scroll hint */}
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="mt-10 flex justify-center"
          >
            <ChevronDown className="w-6 h-6 text-yellow-400 opacity-70" />
          </motion.div>
        </motion.div>
      </section>

      {/* ── MISSION & VISION ── */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          {[
            {
              icon: <Target className="w-9 h-9 text-orange-400" />,
              bg: 'from-orange-500/20 to-red-500/10',
              border: 'border-orange-500/40',
              accent: 'text-orange-400',
              title: 'Our Mission',
              text: 'To deliver innovative, efficient, and customer-focused solutions that empower businesses and individuals.',
            },
            {
              icon: <Globe className="w-9 h-9 text-blue-400" />,
              bg: 'from-blue-500/20 to-cyan-500/10',
              border: 'border-blue-500/40',
              accent: 'text-blue-400',
              title: 'Our Vision',
              text: 'To become a leading innovation-driven company known for creativity, technology, and excellence.',
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ scale: 1.03 }}
              className={`flex items-start gap-5 bg-gradient-to-br ${item.bg} border ${item.border} rounded-2xl p-7 backdrop-blur-sm`}
            >
              <div className="p-3 bg-white/10 rounded-xl flex-shrink-0">{item.icon}</div>
              <div>
                <h2 className={`text-xl font-bold ${item.accent} mb-2`}>{item.title}</h2>
                <p className="text-blue-100 leading-relaxed">{item.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── WHAT WE DO ── */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-5 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-sm font-semibold mb-4">
              What We Do
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white">
              We Specialize In
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {whatWeDo.map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -6, scale: 1.05 }}
                className="flex flex-col items-center text-center p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all duration-300 cursor-default"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white shadow-lg mb-4`}>
                  {item.icon}
                </div>
                <p className="text-white font-semibold text-sm leading-tight">{item.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LEADERSHIP TEAM ── */}
      <section className="py-16 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/40 to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <span className="inline-block px-5 py-2 rounded-full bg-yellow-400/20 border border-yellow-400/30 text-yellow-300 text-sm font-semibold mb-4">
              Leadership
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-3">
              Our Leadership Team
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-orange-400 mx-auto rounded-full" />
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {leadershipTeam.map((member, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <TeamCard member={member} size="md" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MANAGEMENT ── */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <span className="inline-block px-5 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-sm font-semibold mb-4">
              Management
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-3">
              Management &amp; Leadership
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto rounded-full" />
          </motion.div>

          <div className="flex justify-center">
            <div className="w-full max-w-xs">
              <TeamCard member={management[0]} size="lg" />
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="py-16 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/30 to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-5 py-2 rounded-full bg-yellow-400/20 border border-yellow-400/30 text-yellow-300 text-sm font-semibold mb-4">
              Core Values
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white">Our Values</h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-8">
            {values.map((val, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ scale: 1.15, y: -5 }}
                className="flex flex-col items-center gap-3 cursor-default"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${val.color} flex items-center justify-center text-white shadow-xl`}>
                  {val.icon}
                </div>
                <span className="text-blue-100 font-semibold text-sm text-center max-w-[80px]">{val.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-5 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-sm font-semibold mb-4">
              Why Us
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white">Why Choose Us</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {whyUs.map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ x: 6 }}
                className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl px-6 py-4 hover:bg-white/10 hover:border-yellow-400/30 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white flex-shrink-0 shadow-lg">
                  {item.icon}
                </div>
                <span className="text-blue-100 font-medium">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSING STATEMENT ── */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-600/20 rounded-full blur-3xl" />
        </div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto text-center"
        >
          <div className="bg-gradient-to-br from-blue-800/60 to-blue-900/80 border border-blue-500/30 rounded-3xl px-8 py-14 backdrop-blur-sm shadow-2xl">
            {/* Top accent line */}
            <div className="w-20 h-1 bg-gradient-to-r from-yellow-400 to-orange-400 mx-auto rounded-full mb-8" />

            <h2 className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight">
              Turning Ideas into{' '}
              <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                Reality.
              </span>
            </h2>
            <h3 className="text-2xl md:text-3xl font-bold text-blue-200 mb-8">
              Building a Smarter Future.
            </h3>
            <p className="text-blue-100 text-lg leading-relaxed max-w-2xl mx-auto">
              At Agoinnovations, we believe in turning ideas into reality. With a passionate team and strong
              leadership, we are committed to building innovative solutions that create a smarter and better future.
            </p>

            {/* Bottom accent line */}
            <div className="w-20 h-1 bg-gradient-to-r from-yellow-400 to-orange-400 mx-auto rounded-full mt-8" />
          </div>
        </motion.div>
      </section>

      {/* Bottom border */}
      <div className="h-1 bg-gradient-to-r from-yellow-400 via-blue-500 to-yellow-400" />
    </div>
  );
};

export default AboutPage;
